import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { ApiError, errorMessage, get, patch, post } from '../api/client';
import type { Role, SiteId, UserRow } from '../api/types';
import { useMe } from '../auth';
import { IconCopy, IconKey, IconPlus } from '../components/icons';
import { Field, formatDate, Modal, PageHead, Spinner, useFeedback } from '../components/ui';
import { tk, useT } from '../i18n';
import { BRAND, SITE, SITE_LABELS } from '../site';

/** Los textos se traducen con t() al mostrarlos. */
const ROLE_LABEL: Record<Role, string> = { ADMIN: tk('Administrador'), EDITOR: tk('Editor') };
const ALL_SITES: SiteId[] = ['mtx', 'phelon-moore'];
const LOCKED = tk('También entra al dashboard de la otra marca: solo puede cambiarlo un administrador de las dos');

export function Users() {
  const { t, tr } = useT();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const { toast, confirm } = useFeedback();
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);
  const { data, isPending } = useQuery({ queryKey: ['users'], queryFn: () => get<{ items: UserRow[] }>('/admin/users'), enabled: me?.role === 'ADMIN' });

  if (me?.role !== 'ADMIN') return <Navigate to="/" replace />;
  // Quien administra las dos marcas ve (y cambia) a qué dashboards entra cada usuario.
  const both = ALL_SITES.every((s) => me.sites.includes(s));

  const setSites = async (u: UserRow, site: SiteId, on: boolean) => {
    const sites = ALL_SITES.filter((s) => (s === site ? on : u.sites.includes(s)));
    if (!on && site === SITE) {
      const ok = await confirm(
        tr('¿Quitarle a {name} el acceso a este dashboard? Deja de aparecer aquí y su sesión de {brand} se cierra.', { name: <strong>{u.name}</strong>, brand: BRAND.name }),
        { confirmLabel: t('Quitar acceso'), danger: true },
      );
      if (!ok) return;
    }
    await run(
      () => patch(`/admin/users/${u.id}`, { sites }),
      on
        ? t('{name} ya puede entrar al dashboard de {site}', { name: u.name, site: SITE_LABELS[site] })
        : t('{name} ya no entra al dashboard de {site}', { name: u.name, site: SITE_LABELS[site] }),
    );
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
      void qc.invalidateQueries({ queryKey: ['users'] });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  return (
    <>
      <PageHead
        kicker={t('Equipo')}
        title={t('Usuarios')}
        description={
          SITE === 'phelon-moore'
            ? t('Quienes entran a este dashboard. Los editores pueden cambiar noticias, historia y mensajes; los administradores, además, gestionan usuarios y pueden borrar.')
            : t('Quienes entran a este dashboard. Los editores pueden cambiar motos, puntos de venta, textos y mensajes; los administradores, además, gestionan usuarios y pueden borrar.')
        }
      >
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <IconPlus /> {t('Nuevo usuario')}
        </button>
      </PageHead>

      {isPending || !data ? (
        <Spinner />
      ) : (
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>{t('Nombre')}</th>
                <th>{t('Correo')}</th>
                <th>{t('Rol')}</th>
                {both && <th>{t('Dashboards')}</th>}
                <th>{t('Último ingreso')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                  <td>
                    <strong>{u.name}</strong> {u.id === me.id && <span className="badge">{t('Tú')}</span>} {!u.active && <span className="badge">{t('Desactivado')}</span>}{' '}
                    {u.locked && (
                      <span className="badge" title={t(LOCKED)}>
                        {t('También en {site}', { site: SITE_LABELS[BRAND.other] })}
                      </span>
                    )}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      className="select select-sm"
                      style={{ width: 160 }}
                      aria-label={t('Rol de {name}', { name: u.name })}
                      value={u.role}
                      disabled={u.id === me.id || u.locked}
                      title={u.locked ? t(LOCKED) : undefined}
                      onChange={(e) => void run(() => patch(`/admin/users/${u.id}`, { role: e.target.value }), t('Rol actualizado'))}
                    >
                      <option value="EDITOR">{t(ROLE_LABEL.EDITOR)}</option>
                      <option value="ADMIN">{t(ROLE_LABEL.ADMIN)}</option>
                    </select>
                  </td>
                  {both && (
                    <td>
                      <div className="site-toggles">
                        {ALL_SITES.map((s) => (
                          <label key={s} className={`site-toggle ${u.sites.includes(s) ? 'on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={u.sites.includes(s)}
                              // Uno mismo no se quita el dashboard en el que está, y nadie queda sin ninguno.
                              disabled={(u.id === me.id && s === SITE) || (u.sites.length === 1 && u.sites.includes(s))}
                              onChange={(e) => void setSites(u, s, e.target.checked)}
                            />
                            {SITE_LABELS[s]}
                          </label>
                        ))}
                      </div>
                    </td>
                  )}
                  <td className="faint">{u.lastLoginAt ? formatDate(u.lastLoginAt) : t('Nunca')}</td>
                  <td>
                    {u.id !== me.id && !u.locked && (
                      <div className="actions" style={{ justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-sm"
                          onClick={async () => {
                            if (!(await confirm(t('¿Generar una contraseña nueva para {name}? Se le cierran las sesiones abiertas.', { name: u.name }), { confirmLabel: t('Generar') }))) {
                              return;
                            }
                            try {
                              const r = await post<{ password: string }>(`/admin/users/${u.id}/reset-password`);
                              setSecret({ email: u.email, password: r.password });
                            } catch (e) {
                              toast(errorMessage(e), 'error');
                            }
                          }}
                        >
                          <IconKey /> {t('Nueva contraseña')}
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => void run(() => patch(`/admin/users/${u.id}`, { active: !u.active }), u.active ? t('Usuario desactivado') : t('Usuario activado'))}
                        >
                          {u.active ? t('Desactivar') : t('Activar')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateUserDialog
          both={both}
          onClose={() => setCreating(false)}
          onCreated={(email, password) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['users'] });
            if (password) setSecret({ email, password });
            else toast(t('Usuario creado'));
          }}
        />
      )}
      {secret && <PasswordDialog {...secret} onClose={() => setSecret(null)} />}
    </>
  );
}

function CreateUserDialog({ both, onClose, onCreated }: { both: boolean; onClose: () => void; onCreated: (email: string, password?: string) => void }) {
  const { t } = useT();
  const [f, setF] = useState({ name: '', email: '', role: 'EDITOR' as Role, also: false });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ user: UserRow; password?: string }>('/admin/users', {
        name: f.name,
        email: f.email,
        role: f.role,
        // Entra siempre a este dashboard; al de la otra marca, solo si se marca.
        sites: f.also ? [SITE, BRAND.other] : [SITE],
      });
      onCreated(r.user.email, r.password);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('Nuevo usuario')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('Cancelar')}
          </button>
          <button className="btn btn-primary" disabled={busy || !f.name || !f.email} onClick={submit}>
            {t('Crear')}
          </button>
        </>
      }
    >
      {error && !error.errors.length && <div className="notice error">{error.message}</div>}
      <Field label={t('Nombre')} error={error?.field('name')}>
        {(id) => <input id={id} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
      </Field>
      <Field label={t('Correo')} error={error?.field('email')}>
        {(id) => <input id={id} type="email" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}
      </Field>
      <Field label={t('Rol')}>
        {(id) => (
          <select id={id} className="select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>
            <option value="EDITOR">{t(ROLE_LABEL.EDITOR)}</option>
            <option value="ADMIN">{t(ROLE_LABEL.ADMIN)}</option>
          </select>
        )}
      </Field>
      {both && (
        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.also} onChange={(e) => setF({ ...f, also: e.target.checked })} />
          {t('También puede entrar al dashboard de {site}', { site: SITE_LABELS[BRAND.other] })}
        </label>
      )}
      <p className="faint" style={{ margin: 0 }}>
        {t('Entra al dashboard de {brand}. Se genera una contraseña segura que verás una sola vez para enviársela.', { brand: BRAND.name })}
      </p>
    </Modal>
  );
}

function PasswordDialog({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const { t, tr } = useT();
  const { toast } = useFeedback();
  return (
    <Modal
      title={t('Contraseña generada')}
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          {t('Listo')}
        </button>
      }
    >
      <p style={{ margin: 0 }}>
        {tr('Envíasela a {email} por un canal privado.', { email: <strong>{email}</strong> })} <span className="muted">{t('No se vuelve a mostrar.')}</span>
      </p>
      <div className="row">
        <input className="input mono" readOnly value={password} onFocus={(e) => e.target.select()} />
        <button
          className="btn"
          onClick={async () => {
            await navigator.clipboard.writeText(password);
            toast(t('Copiada'));
          }}
        >
          <IconCopy /> {t('Copiar')}
        </button>
      </div>
    </Modal>
  );
}
